import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Props, TextField } from "@/components/TextField/TextField";

export const delimiter = " "; // スペース区切り(例: 090 1234 5678)

export const TextFieldPhoneNumber: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [isKeyDownBackcpace, setIsKeyDownBackcpace] = useState(false);
  const [isPasteMode, setIsPasteMode] = useState(false);

  const maxLength = useMemo(() => {
    return isPasteMode ? 11 : 13; // ペースト時は11、通常入力時は13
  }, [isPasteMode]);

  const formatPhoneNumber = (inputValue: string): string => {
    const v = String(inputValue || "");

    if (v.match(`^[0-9]{11}$`)) {
      const areaCode = v.slice(0, 3); // 市外局番
      const localCode = v.slice(3, 7); // 市内局番
      const subscriberCode = v.slice(7, 11); // 加入者番号
      return `${areaCode}${delimiter}${localCode}${delimiter}${subscriberCode}`;
    }

    if (!isKeyDownBackcpace) {
      if (v.match(`^[0-9]{3}$`)) {
        return v + delimiter;
      } else if (v.match(`^[0-9]{4}$`)) {
        return v.slice(0, 3) + delimiter + v.slice(3, 4);
      } else if (v.match(`^[0-9]{3}${delimiter}[0-9]{4}$`)) {
        return v + delimiter;
      } else if (v.match(`^[0-9]{3}${delimiter}[0-9]{5}$`)) {
        return v.slice(0, 8) + delimiter + v.slice(8, 9);
      }
    }

    return v;
  };

  const displayValue = formatPhoneNumber(String(props.value || ""));

  const _onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const v = e.clipboardData.getData("text/plain");
    if (v.match(`^[0-9]+$`)) {
      setIsPasteMode(true);
      setTimeout(() => setIsPasteMode(false), 0);
    }
  };

  // value変換(スペース自動削除: backspaceのKeyUp時に末尾の区切り文字を削除)
  const _onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && props.onChange) {
      const currentValue = (e.target as HTMLInputElement).value;
      if (currentValue.match(`${delimiter}$`)) {
        const newValue = currentValue.slice(0, -1);
        // 親コンポーネントに変更を通知
        const target = e.target as HTMLInputElement;
        target.value = newValue;
        const changeEvent = new Event("change", { bubbles: true }) as unknown as React.ChangeEvent<HTMLInputElement>;
        Object.defineProperty(changeEvent, "target", { value: target, enumerable: true });
        props.onChange(changeEvent);
      }
    }
  };

  // value変換(backspaceのKeyDown時は変換しない)
  const _onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setIsKeyDownBackcpace(e.key === "Backspace");
    if (isPasteMode) {
      setIsPasteMode(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatPhoneNumber(inputValue);

    if (props.onChange) {
      const target = e.target;
      target.value = formattedValue;
      const changeEvent = { ...e, target } as React.ChangeEvent<HTMLInputElement>;
      props.onChange(changeEvent);
    }
  };

  return (
    <TextField
      {...props}
      onPaste={_onPaste}
      onKeyUp={_onKeyUp}
      onKeyDown={_onKeyDown}
      onChange={handleChange}
      value={displayValue}
      placeholder={t("PHONE_NUMBER_EXAMPLE")}
      inputProps={{ maxLength: maxLength }}
    />
  );
};
